import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from '@shared/ui/Input';

export type PasswordInputProps = Omit<InputProps, 'type' | 'trailing'>;

/**
 * A password field with a show/hide toggle.
 *
 * The toggle is a `button` with `tabIndex={-1}`: it is reachable by pointer but
 * skipped when tabbing, so it does not sit between the password field and the
 * submit button. Screen readers still get its state from `aria-pressed`.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(props, ref) {
    const [isVisible, setVisible] = useState(false);
    const Icon = isVisible ? EyeOff : Eye;

    return (
      <Input
        ref={ref}
        type={isVisible ? 'text' : 'password'}
        trailing={
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setVisible((visible) => !visible)}
            aria-label={isVisible ? 'Hide password' : 'Show password'}
            aria-pressed={isVisible}
            className="flex h-7 w-7 items-center justify-center rounded text-slate-400 transition-colors hover:text-slate-700"
          >
            <Icon aria-hidden className="h-4 w-4" />
          </button>
        }
        {...props}
      />
    );
  },
);
